import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

export const AdminBackdoorPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithData } = useAuth();
  const [secretKey, setSecretKey] = useState(searchParams.get('key') || '');
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('login'); // 'login' or 'reset'

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!secretKey.trim()) {
      toast.error('Please enter the secret key');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/auth/admin-backdoor`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ secret_key: secretKey }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success('Admin access granted!');
        loginWithData(data);
        navigate('/admin');
      } else {
        toast.error(data.detail || 'Invalid secret key');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!secretKey.trim()) {
      toast.error('Please enter the secret key');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/auth/emergency-reset-admin`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ secret_key: secretKey }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(`Password reset! Temporary password: ${data.temporary_password}`);
        // Show the reset info
        alert(`Admin password has been reset!\n\nEmail: ${data.email}\nTemporary Password: ${data.temporary_password}\n\nPlease login and change your password immediately.`);
        navigate('/');
      } else {
        toast.error(data.detail || 'Invalid secret key');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <span className="text-xl font-bold text-white">Admin Portal</span>
          </div>
          <CardTitle className="text-xl text-white">Emergency Access</CardTitle>
          <p className="text-gray-400 text-sm mt-2">This is a restricted area. Unauthorized access is prohibited.</p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-6">
            <Button
              variant={action === 'login' ? 'default' : 'outline'}
              onClick={() => setAction('login')}
              className={`flex-1 ${action === 'login' ? 'bg-green-700' : 'bg-transparent text-gray-300 border-gray-600'}`}
              data-testid="login-tab"
            >
              Quick Login
            </Button>
            <Button
              variant={action === 'reset' ? 'default' : 'outline'}
              onClick={() => setAction('reset')}
              className={`flex-1 ${action === 'reset' ? 'bg-red-600' : 'bg-transparent text-gray-300 border-gray-600'}`}
              data-testid="reset-tab"
            >
              Reset Password
            </Button>
          </div>

          <form onSubmit={action === 'login' ? handleAdminLogin : handlePasswordReset} className="space-y-4">
            <div>
              <Label htmlFor="secretKey" className="text-gray-300">Secret Key</Label>
              <Input
                id="secretKey"
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="Enter admin secret key"
                required
                className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                data-testid="secret-key-input"
              />
            </div>

            <Button 
              type="submit" 
              className={`w-full ${action === 'login' ? 'bg-green-700 hover:bg-green-800' : 'bg-red-600 hover:bg-red-700'}`}
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Processing...' : action === 'login' ? 'Access Admin Panel' : 'Reset Admin Password'}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-700">
            <Button 
              variant="link" 
              onClick={() => navigate('/')} 
              className="w-full text-gray-400 hover:text-white"
              data-testid="back-home-btn"
            >
              ← Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBackdoorPage;
